import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Panel } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";

import { useGetMemberMedicareAmountQuery, useListMemberAccumulationsQuery } from "@/App/api/member";
import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useIsMedicareUser } from "@/App/hooks";
import { enableMedicarePrescriptionPaymentPlan } from "@/App/utils/featureFlags";
import { formatAmount, subtract } from "@/App/utils/utils";
import { MedicareCostBreakdown } from "./MedicareCostBreakdown";
import { MedicareOverview } from "./MedicareOverview";
import { MedicarePrescriptionPaymentPlan } from "./MedicarePrescriptionPaymentPlan";

export const MedicareBenefits: FC = () => {
  const { t: translate } = useTranslation();

  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { hideMedicarePrescriptionPaymentPlan } = useContext(ExperienceContext);

  const { isPartD, isPartB } = useIsMedicareUser();

  const { data: memberAccumulations, isLoading: isLoadingMemberAccumulations } = useListMemberAccumulationsQuery(true);
  /* TODO: we will eventually need to address mediccal accumulation balances with medicare so please dont make me remove this
  const { data: memberUnifiedAccumulations, isLoading: isLoadingMemberUnifiedAccumulations } = useListMemberUnifiedAccumulationsQuery(); */
  const { data: medicareAmount } = useGetMemberMedicareAmountQuery({
    year: new Date().getFullYear(),
  });


  const medicarePartDData = useMemo(() => {
    let deductible_spent = formatAmount(memberAccumulations?.deductible?.individual_amount);
    let deductible_remaining = subtract(
      memberAccumulations?.deductible?.individual_max_amount,
      memberAccumulations?.deductible?.individual_amount,
    );

    if ((medicareAmount?.deductible_phase_maximum || "0").toString() !== memberAccumulations?.deductible?.individual_max_amount) {
      deductible_spent = formatAmount(memberAccumulations?.gross_covered_drug_cost?.individual_amount);
      deductible_remaining = subtract(
        memberAccumulations?.deductible?.individual_max_amount,
        memberAccumulations?.gross_covered_drug_cost?.individual_amount,
      );
    }

    const deductible_max = formatAmount(memberAccumulations?.deductible?.individual_max_amount);

    return {
      Deductible: {
        spent: deductible_spent,
        remaining: deductible_remaining,
        max: deductible_max,
        text: translate("BENEFITS.medicareBenefits.deductibleText"),
      },
      "Gross Covered Drug Cost": {
        spent: formatAmount(memberAccumulations?.gross_covered_drug_cost?.individual_amount),
        remaining: subtract(
          memberAccumulations?.gross_covered_drug_cost?.individual_max_amount,
          memberAccumulations?.gross_covered_drug_cost?.individual_amount,
        ),
        max: formatAmount(memberAccumulations?.gross_covered_drug_cost?.individual_max_amount),
        text: translate("BENEFITS.medicareBenefits.grossCoveredDrugCostText"),
      },
      "True Out-of-Pocket": {
        spent: formatAmount(memberAccumulations?.true_out_of_pocket?.individual_amount),
        remaining: subtract(
          memberAccumulations?.true_out_of_pocket?.individual_max_amount,
          memberAccumulations?.true_out_of_pocket?.individual_amount,
        ),
        max: formatAmount(memberAccumulations?.true_out_of_pocket?.individual_max_amount),
        text: translate("BENEFITS.medicareBenefits.trueOutOfPocketText"),
      },
    };
  }, [memberAccumulations, medicareAmount]);

  const medicarePartBData = useMemo(() => {
    return {
      Deductible: {
        spent: formatAmount(memberAccumulations?.part_b_deductible?.individual_amount),
        remaining: subtract(
          memberAccumulations?.part_b_deductible?.individual_max_amount,
          memberAccumulations?.part_b_deductible?.individual_amount,
        ),
        max: formatAmount(memberAccumulations?.part_b_deductible?.individual_max_amount),
        text: translate("BENEFITS.medicareBenefits.deductibleText"),
      },
      "Yearly Out-of-Pocket Limit": {
        spent: formatAmount(memberAccumulations?.part_b_maximum_out_of_pocket?.individual_amount),
        remaining: subtract(
          memberAccumulations?.part_b_maximum_out_of_pocket?.individual_max_amount,
          memberAccumulations?.part_b_maximum_out_of_pocket?.individual_amount,
        ),
        max: formatAmount(memberAccumulations?.part_b_maximum_out_of_pocket?.individual_max_amount),
        text: translate("BENEFITS.medicareBenefits.yearlyOutOfPocketLimitText"),
      },
    };
  }, [memberAccumulations, medicareAmount]);

  const showMedicarePrescriptionPaymentPlan = useMemo(() => {
    return enableMedicarePrescriptionPaymentPlan() && isPartD && !hideMedicarePrescriptionPaymentPlan;
  }, [memberEligibility, isPartD, hideMedicarePrescriptionPaymentPlan]);

  return (
    <Stack>
      {showMedicarePrescriptionPaymentPlan && (
        <Panel>
          <MedicarePrescriptionPaymentPlan />
        </Panel>
      )}
      {(isPartB || isPartD) && (
        <Panel>
          <MedicareOverview
            hasPartB={isPartB}
            hasPartD={isPartD}
            medicareAmount={medicareAmount}
            memberAccumulations={memberAccumulations}
            isLoadingMemberAccumulations={isLoadingMemberAccumulations}
          />
        </Panel>
      )}
      {isPartD && (
        <Panel>
          <MedicareCostBreakdown type="partD" data={medicarePartDData || {}} />
        </Panel>
      )}
      {isPartB && (
        <Panel>
          <MedicareCostBreakdown type="partB" data={medicarePartBData || {}} />
        </Panel>
      )}
    </Stack>
  );
};
